import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { Search, UserCheck, History, ArrowRightLeft, X, User } from 'lucide-react';
import VehicleChecklistForm from './VehicleChecklistForm';
import VehicleChecklistViewer from './VehicleChecklistViewer';
import { createChecklistState } from './vehicleChecklistConfig';

const parseChecklist = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
};

export default function AsignacionVehiculosModule() {
  const now = new Date();
  const todayYmd = now.toISOString().split('T')[0];
  const currentTime = now.toTimeString().slice(0, 5);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [driverQuery, setDriverQuery] = useState('');
  const [driversLoading, setDriversLoading] = useState(false);
  const [driversOpen, setDriversOpen] = useState(false);
  const [assignDriverError, setAssignDriverError] = useState('');
  
  // Modal states
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [checklistViewer, setChecklistViewer] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [returnHistory, setReturnHistory] = useState([]);
  const [loadingReturnHistory, setLoadingReturnHistory] = useState(false);
  
  // Form data
  const [assignData, setAssignData] = useState({
    driver_id: null,
    person_id: null,
    driver_name: '',
    city: '',
    date: todayYmd,
    time: currentTime,
    notes: '',
    checklist: null,
  });
  const [returnData, setReturnData] = useState({
    date: todayYmd,
    time: currentTime,
    return_notes: '',
    checklist: null,
  });

  const formatDateTime = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '-';
    const normalized = raw.includes(' ') ? raw.replace(' ', 'T') : raw;
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) return raw;
    try {
      return parsed.toLocaleString('es-CO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (_) {
      return raw;
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!isAssignModalOpen) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setDriversLoading(true);
      try {
        const res = await api.conductores.get({ q: driverQuery || '' });
        if (!cancelled) {
          setDrivers(Array.isArray(res?.data) ? res.data : []);
        }
      } catch (_) {
        if (!cancelled) setDrivers([]);
      } finally {
        if (!cancelled) setDriversLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [driverQuery, isAssignModalOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.vehicleAssignments.get();
      if (res.ok) {
        setVehicles(res.data || []);
      }
    } catch (error) {
      console.error("Error fetching assignments:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVehicles = vehicles.filter(v => 
    v.plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.brand?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openAssign = (vehicle) => {
    if (!vehicle?.vehicle_type) {
      alert('Este vehículo no tiene tipo configurado. Actualízalo primero en Flota.');
      return;
    }
    setSelectedVehicle(vehicle);
    setAssignData({
        driver_id: null,
        person_id: null,
        driver_name: '',
        city: '',
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        notes: '',
        checklist: createChecklistState(vehicle.vehicle_type),
    });
    setDriverQuery('');
    setDriversOpen(false);
    setAssignDriverError('');
    setIsAssignModalOpen(true);
  };

  const openReturn = (vehicle, assignment) => {
      if (!vehicle?.vehicle_type) {
        alert('Este vehículo no tiene tipo configurado. Actualízalo primero en Flota.');
        return;
      }
      setSelectedVehicle(vehicle);
      setSelectedAssignment(assignment);
      setReturnHistory([]);
      setLoadingReturnHistory(true);
      setReturnData({
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        return_notes: '',
        checklist: createChecklistState(vehicle.vehicle_type),
      });
      setIsReturnModalOpen(true);
      // El listado principal ya no trae assignment_checklist (pesaba ~76MB en
      // base64 entre las asignaciones activas y hacía lento el módulo), así
      // que el checklist de la asignación original para comparar en la
      // devolución se toma de este history, que sí lo incluye completo.
      api.vehicleAssignments.history(vehicle.id)
        .then((res) => {
          const rows = Array.isArray(res?.data) ? res.data : [];
          setReturnHistory(rows);
          const match = assignment?.id ? rows.find((r) => r.id === assignment.id) : null;
          if (match?.assignment_checklist) {
            setReturnData((prev) => ({
              ...prev,
              checklist: createChecklistState(vehicle.vehicle_type, parseChecklist(match.assignment_checklist)),
            }));
          }
        })
        .catch(() => {
          setReturnHistory([]);
        })
        .finally(() => {
          setLoadingReturnHistory(false);
        });
  };

  const handleReturnSubmit = async (e) => {
      e.preventDefault();
      if (!selectedVehicle || !selectedAssignment) return;
      try {
          const res = await api.vehicleAssignments.return({
            vehicle_id: selectedVehicle.id,
            assignment_id: selectedAssignment.id,
            date: returnData.date,
            time: returnData.time,
            return_notes: returnData.return_notes,
            checklist: returnData.checklist,
          });
          if (res.ok) {
            setIsReturnModalOpen(false);
            setSelectedAssignment(null);
            fetchData();
          } else {
            alert("Error: " + (res.error || 'No fue posible registrar la devolución'));
          }
      } catch (e) {
          // api.request() rechaza (throw) cuando el backend responde ok:false,
          // así que ese mensaje real llega aquí como e.message, no en res.error
          // (esa rama de arriba queda inalcanzable). Antes se descartaba y solo
          // se mostraba un texto genérico, ocultando la razón real del rechazo
          // (p. ej. límites de asignación, validación, etc.).
          alert("Error: " + (e?.message || "No fue posible registrar la devolución"));
      }
  };

  const openHistory = async (vehicle) => {
      setSelectedVehicle(vehicle);
      setIsHistoryOpen(true);
      setHistoryData([]);
      try {
          const res = await api.vehicleAssignments.history(vehicle.id);
          if (res.ok) {
              setHistoryData(res.data || []);
          }
      } catch (e) {
          console.error(e);
      }
  };

  const openChecklistViewer = (record, source) => {
    const rawChecklist = source === 'assignment' ? record?.assignment_checklist : record?.return_checklist;
    const checklist = parseChecklist(rawChecklist);
    if (!checklist) {
      alert(`No hay checklist de ${source === 'assignment' ? 'asignación' : 'devolución'} para este registro.`);
      return;
    }

    setChecklistViewer({
      source,
      record,
      checklist,
      vehicleType: selectedVehicle?.vehicle_type || checklist?.vehicle_type || '',
    });
  };

  const handleAssignSubmit = async (e) => {
      e.preventDefault();
      if (!selectedVehicle) return;
      
      try {
          const res = await api.vehicleAssignments.assign({
              vehicle_id: selectedVehicle.id,
              ...assignData,
          });
          if (res.ok) {
              setIsAssignModalOpen(false);
              fetchData();
          } else {
              alert("Error: " + res.error);
          }
      } catch (e) {
          // Ver comentario equivalente en handleReturnSubmit: el mensaje real
          // del backend (p. ej. "Este vehículo ya tiene el máximo de 2
          // conductores asignados.") llega en e.message, no en res.error.
          alert("Error: " + (e?.message || "Error al asignar"));
      }
  };

  return (
    <div className="p-4 space-y-6">
      <div className="bg-white p-4 rounded-xl shadow border border-gray-200 flex flex-col md:flex-row gap-4 justify-between items-center">
         <h2 className="text-lg font-semibold flex items-center gap-2">
             <UserCheck className="w-5 h-5 text-blue-600" />
             Asignación de Vehículos
         </h2>
         <div className="relative w-full md:w-64">
             <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
             <input 
               type="text" 
               placeholder="Buscar placa..." 
               className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm"
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
             />
         </div>
      </div>

      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehículo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conductor Actual</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ciudad</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Desde</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="6" className="px-6 py-10 text-center">Cargando...</td></tr>
              ) : filteredVehicles.map((v) => {
                  const assignments = Array.isArray(v.current_assignments) ? v.current_assignments : [];
                  const hasSlot = assignments.length < 2;
                  return (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-900">{v.plate}</span>
                            <span className="text-xs text-gray-500">{v.brand} {v.model}</span>
                        </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{v.vehicle_type || '-'}</td>
                    <td className="px-6 py-4">
                        {assignments.length > 0 ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Asignado ({assignments.length}/2)
                            </span>
                        ) : (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                Disponible
                            </span>
                        )}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {assignments.length > 0 ? (
                          <div className="flex flex-col gap-1">
                            {assignments.map((a) => (
                              <div key={a.id} className="flex items-center gap-2">
                                <span>{a.driver_name || '-'}</span>
                                <button
                                  onClick={() => openReturn(v, a)}
                                  className="p-0.5 text-red-600 hover:bg-red-50 rounded"
                                  title="Devolver Vehículo"
                                >
                                  <ArrowRightLeft className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex flex-col gap-1">
                          {assignments.length > 0 ? assignments.map((a) => (
                            <span key={a.id}>{a.city || '-'}</span>
                          )) : '-'}
                        </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex flex-col gap-1">
                          {assignments.length > 0 ? assignments.map((a) => (
                            <span key={a.id}>{a.assigned_at || '-'}</span>
                          )) : '-'}
                        </div>
                    </td>
                    <td className="px-6 py-4 flex gap-2">
                        {hasSlot && (
                             <button
                                onClick={() => openAssign(v)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded flex items-center gap-1"
                                title="Asignar Vehículo"
                             >
                                <User className="w-4 h-4" />
                             </button>
                        )}
                        <button
                            onClick={() => openHistory(v)}
                            className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                            title="Ver Historial"
                        >
                            <History className="w-5 h-5" />
                        </button>
                    </td>
                  </tr>
                  );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign Modal */}
      {isAssignModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 p-4 sm:p-6">
              <div className="mx-auto my-2 w-full max-w-6xl rounded-lg bg-white shadow-xl max-h-none min-h-0 flex flex-col">
                  <div className="flex justify-between items-center p-6 border-b">
                      <h3 className="text-lg font-bold">Asignar Vehículo - {selectedVehicle?.plate}</h3>
                      <button onClick={() => setIsAssignModalOpen(false)}><X className="w-5 h-5" /></button>
                  </div>
                  <form onSubmit={handleAssignSubmit} className="p-6 space-y-4">
                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                        Tipo de vehículo detectado: <span className="font-semibold">{selectedVehicle?.vehicle_type}</span>.
                        El checklist se ajusta automáticamente al formato correspondiente.
                      </div>
                      <div>
                          <label className="block text-sm font-medium mb-1">Nombre del Conductor</label>
                          <div className="relative">
                            <input
                              type="text"
                              className="w-full border rounded p-2"
                              required
                              value={assignData.driver_name}
                              onChange={e => {
                                const v = e.target.value;
                                setAssignData({ ...assignData, driver_name: v, driver_id: null, person_id: null });
                                setAssignDriverError('');
                                setDriverQuery(v);
                                setDriversOpen(true);
                              }}
                              onFocus={() => setDriversOpen(true)}
                              onBlur={() => setTimeout(() => setDriversOpen(false), 120)}
                              placeholder={driversLoading ? 'Cargando conductores…' : 'Buscar conductor por nombre o cédula'}
                            />
                            {driversOpen && (driversLoading || drivers.length > 0 || driverQuery.trim() !== '') && (
                              <div className="absolute z-10 mt-1 w-full max-h-56 overflow-auto bg-white border rounded shadow">
                                {driversLoading ? (
                                  <div className="px-3 py-2 text-sm text-gray-500">Cargando…</div>
                                ) : (drivers.length === 0 ? (
                                  <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
                                ) : (
                                  drivers.map((d) => (
                                    <button
                                      key={d.id}
                                      type="button"
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                      onMouseDown={(ev) => ev.preventDefault()}
                                      onClick={() => {
                                        if (!d.person_id) {
                                          setAssignDriverError(
                                            'Este conductor no está vinculado a un colaborador de RRHH. Vincúlelo en Transportes > Conductores antes de asignar el vehículo.'
                                          );
                                          setAssignData({
                                            ...assignData,
                                            driver_name: d.nombre || '',
                                            driver_id: d.id || null,
                                            person_id: null,
                                          });
                                          setDriverQuery(d.nombre || '');
                                          setDriversOpen(false);
                                          return;
                                        }
                                        setAssignDriverError('');
                                        setAssignData({
                                          ...assignData,
                                          driver_name: d.nombre || '',
                                          driver_id: d.id || null,
                                          person_id: d.person_id || null,
                                        });
                                        setDriverQuery(d.nombre || '');
                                        setDriversOpen(false);
                                      }}
                                    >
                                      <div className="font-medium text-gray-900">{d.nombre || '—'}</div>
                                      <div className="text-xs text-gray-500">{d.cedula || d.usuario || ''}</div>
                                    </button>
                                  ))
                                ))}
                              </div>
                            )}
                          </div>
                          {assignDriverError ? (
                            <p className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2" role="alert">
                              {assignDriverError}
                            </p>
                          ) : null}
                          {!assignData.person_id && assignData.driver_name.trim() !== '' && !assignDriverError ? (
                            <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                              Seleccione un conductor de la lista (debe tener colaborador de RRHH vinculado).
                            </p>
                          ) : null}
                      </div>
                      <div>
                          <label className="block text-sm font-medium mb-1">Asignar ciudad</label>
                          <select
                            className="w-full border rounded p-2"
                            required
                            value={assignData.city}
                            onChange={(e) => setAssignData({ ...assignData, city: e.target.value })}
                          >
                            <option value="">Seleccionar ciudad</option>
                            <option value="Popayán">Popayán</option>
                            <option value="Santander de Quilichao">Santander de Quilichao</option>
                            <option value="Cali">Cali</option>
                          </select>
                      </div>
                      <div>
                          <label className="block text-sm font-medium mb-1">Fecha de Asignación</label>
                          <input type="date" className="w-full border rounded p-2" required 
                              value={assignData.date} onChange={e => setAssignData({...assignData, date: e.target.value})} />
                      </div>
                      <div>
                          <label className="block text-sm font-medium mb-1">Hora de Asignación</label>
                          <input type="time" className="w-full border rounded p-2" required
                              value={assignData.time} onChange={e => setAssignData({...assignData, time: e.target.value})} />
                      </div>
                      <div>
                          <label className="block text-sm font-medium mb-1">Notas</label>
                          <textarea className="w-full border rounded p-2" rows="3"
                              value={assignData.notes} onChange={e => setAssignData({...assignData, notes: e.target.value})}></textarea>
                      </div>
                      <VehicleChecklistForm
                        vehicleType={selectedVehicle?.vehicle_type || ''}
                        modeLabel="Asignación"
                        value={assignData.checklist}
                        onChange={(checklist) => setAssignData((prev) => ({ ...prev, checklist }))}
                      />
                      <div className="sticky bottom-0 z-10 border-t border-gray-200 bg-white/95 px-1 py-3 backdrop-blur">
                        <button
                          type="submit"
                          disabled={!assignData.person_id}
                          className="w-full text-white py-2 rounded hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ backgroundColor: '#F07E2D' }}
                        >
                          Asignar
                        </button>
                      </div>
                  </form>
              </div>
          </div>
      )}

      {/* Return Modal */}
      {isReturnModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 p-4 sm:p-6">
              <div className="mx-auto my-2 w-full max-w-6xl rounded-lg bg-white shadow-xl max-h-none min-h-0 flex flex-col">
                  <div className="flex justify-between items-center p-6 border-b">
                      <h3 className="text-lg font-bold">Devolución de Vehículo - {selectedVehicle?.plate}</h3>
                      <button onClick={() => { setIsReturnModalOpen(false); setSelectedAssignment(null); }}><X className="w-5 h-5" /></button>
                  </div>
                  <form onSubmit={handleReturnSubmit} className="p-6 space-y-4">
                      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                        Tipo de vehículo detectado: <span className="font-semibold">{selectedVehicle?.vehicle_type}</span>.
                        El mismo formato del tipo de vehículo se utiliza para registrar la devolución.
                      </div>
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                          <div className="text-sm font-semibold text-gray-900 mb-3">Datos actuales de la asignación</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div>
                              <div className="text-xs uppercase tracking-wide text-gray-500">Conductor</div>
                              <div className="mt-1 font-medium text-gray-900">{selectedAssignment?.driver_name || '-'}</div>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wide text-gray-500">Ciudad</div>
                              <div className="mt-1 font-medium text-gray-900">{selectedAssignment?.city || '-'}</div>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wide text-gray-500">Asignado desde</div>
                              <div className="mt-1 font-medium text-gray-900">{formatDateTime(selectedAssignment?.assigned_at)}</div>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wide text-gray-500">Estado</div>
                              <div className="mt-1 font-medium text-gray-900">{selectedAssignment ? 'Activo' : '-'}</div>
                            </div>
                            <div className="md:col-span-2">
                              <div className="text-xs uppercase tracking-wide text-gray-500">Notas de asignación</div>
                              <div className="mt-1 whitespace-pre-wrap text-gray-800">{selectedAssignment?.notes || 'Sin observaciones registradas.'}</div>
                            </div>
                          </div>
                        </div>
                        <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
                          <div className="text-sm font-semibold text-gray-900 mb-3">Historial reciente</div>
                          {loadingReturnHistory ? (
                            <div className="text-sm text-gray-600">Cargando historial...</div>
                          ) : returnHistory.length === 0 ? (
                            <div className="text-sm text-gray-600">No hay historial previo para este vehículo.</div>
                          ) : (
                            <div className="space-y-3">
                              {returnHistory.slice(0, 3).map((row) => (
                                <div key={row.id} className="rounded-lg border border-orange-100 bg-white p-3 text-sm">
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="font-medium text-gray-900">{row.driver_name || 'Sin conductor'}</div>
                                    <div className="text-xs text-gray-500">{row.returned_at ? 'Devuelto' : 'Asignación actual'}</div>
                                  </div>
                                  <div className="mt-1 text-xs text-gray-500">
                                    {[row.city ? `Ciudad: ${row.city}` : '', row.assigned_at ? `Asignado: ${formatDateTime(row.assigned_at)}` : '', row.returned_at ? `Devuelto: ${formatDateTime(row.returned_at)}` : ''].filter(Boolean).join(' · ')}
                                  </div>
                                  <div className="mt-2 text-xs text-gray-700">
                                    <span className="font-semibold">Asignación:</span> {row.notes || 'Sin notas'}
                                  </div>
                                  <div className="mt-1 text-xs text-gray-700">
                                    <span className="font-semibold">Devolución:</span> {row.return_notes || 'Sin observaciones'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                          <label className="block text-sm font-medium mb-1">Fecha de Devolución</label>
                          <input
                            type="date"
                            className="w-full border rounded p-2"
                            required
                            value={returnData.date}
                            onChange={(e) => setReturnData({ ...returnData, date: e.target.value })}
                          />
                      </div>
                      <div>
                          <label className="block text-sm font-medium mb-1">Hora de Devolución</label>
                          <input
                            type="time"
                            className="w-full border rounded p-2"
                            required
                            value={returnData.time}
                            onChange={(e) => setReturnData({ ...returnData, time: e.target.value })}
                          />
                      </div>
                      <div>
                          <label className="block text-sm font-medium mb-1">Observaciones de Devolución</label>
                          <textarea
                            className="w-full border rounded p-2"
                            rows="4"
                            placeholder="Relaciona novedades, estado del vehículo u observaciones de entrega"
                            value={returnData.return_notes}
                            onChange={(e) => setReturnData({ ...returnData, return_notes: e.target.value })}
                          />
                      </div>
                      <VehicleChecklistForm
                        vehicleType={selectedVehicle?.vehicle_type || ''}
                        modeLabel="Devolución"
                        value={returnData.checklist}
                        onChange={(checklist) => setReturnData((prev) => ({ ...prev, checklist }))}
                      />
                      <div className="sticky bottom-0 z-10 border-t border-gray-200 bg-white/95 px-1 py-3 backdrop-blur">
                        <button type="submit" className="w-full text-white py-2 rounded hover:bg-red-700 bg-red-600">Registrar devolución</button>
                      </div>
                  </form>
              </div>
          </div>
      )}

      {/* History Modal */}
      {isHistoryOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 p-4 sm:p-6">
              <div className="mx-auto my-2 w-full max-w-6xl rounded-lg bg-white shadow-xl max-h-none flex flex-col">
                  <div className="p-4 border-b flex justify-between items-center">
                      <h3 className="text-lg font-bold">Historial de Asignaciones - {selectedVehicle?.plate}</h3>
                      <button onClick={() => setIsHistoryOpen(false)}><X className="w-5 h-5" /></button>
                  </div>
                  <div className="p-4 overflow-x-auto">
                      {historyData.length === 0 ? (
                          <p className="text-gray-500 text-center">No hay registros.</p>
                      ) : (
                          <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                  <tr>
                                      <th className="p-2 text-left">Conductor</th>
                                      <th className="p-2 text-left">Ciudad</th>
                                      <th className="p-2 text-left">Fecha y Hora Asignación</th>
                                      <th className="p-2 text-left">Fecha y Hora Devolución</th>
                                      <th className="p-2 text-left">Notas Asignación</th>
                                      <th className="p-2 text-left">Observaciones Devolución</th>
                                      <th className="p-2 text-left">Checklist</th>
                                  </tr>
                              </thead>
                              <tbody>
                                  {historyData.map(h => (
                                      <tr key={h.id} className="border-b">
                                          <td className="p-2 font-medium">{h.driver_name}</td>
                                          <td className="p-2">{h.city || '-'}</td>
                                          <td className="p-2">{formatDateTime(h.assigned_at)}</td>
                                          <td className="p-2">{h.returned_at ? formatDateTime(h.returned_at) : 'Actual'}</td>
                                          <td className="p-2 text-gray-500">{h.notes || '-'}</td>
                                          <td className="p-2 text-gray-500">{h.return_notes || '-'}</td>
                                          <td className="p-2 text-gray-500">
                                            <div className="flex flex-col gap-2">
                                              <button
                                                type="button"
                                                disabled={!h.assignment_checklist}
                                                onClick={() => openChecklistViewer(h, 'assignment')}
                                                className={`rounded px-3 py-1 text-left text-xs font-medium ${
                                                  h.assignment_checklist
                                                    ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                                    : 'cursor-not-allowed bg-gray-100 text-gray-400'
                                                }`}
                                              >
                                                Ver asignación
                                              </button>
                                              <button
                                                type="button"
                                                disabled={!h.return_checklist}
                                                onClick={() => openChecklistViewer(h, 'return')}
                                                className={`rounded px-3 py-1 text-left text-xs font-medium ${
                                                  h.return_checklist
                                                    ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                                    : 'cursor-not-allowed bg-gray-100 text-gray-400'
                                                }`}
                                              >
                                                Ver devolución
                                              </button>
                                            </div>
                                          </td>
                                      </tr>
                                  ))}
                              </tbody>
                          </table>
                      )}
                  </div>
              </div>
          </div>
      )}

      {checklistViewer && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 p-4 sm:p-6">
              <div className="mx-auto my-2 w-full max-w-6xl rounded-lg bg-white shadow-xl max-h-none flex flex-col">
                  <div className="p-4 border-b flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-bold">
                          Checklist de {checklistViewer.source === 'assignment' ? 'Asignación' : 'Devolución'} - {selectedVehicle?.plate}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Conductor: {checklistViewer.record?.driver_name || '-'} | Ciudad: {checklistViewer.record?.city || '-'}
                        </p>
                      </div>
                      <button onClick={() => setChecklistViewer(null)}><X className="w-5 h-5" /></button>
                  </div>
                  <div className="p-4">
                    <VehicleChecklistViewer
                      checklist={checklistViewer.checklist}
                      vehicleType={checklistViewer.vehicleType}
                      modeLabel={checklistViewer.source === 'assignment' ? 'Asignación' : 'Devolución'}
                    />
                  </div>
              </div>
          </div>
      )}
    </div>
  );
}
